import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { startWith, debounceTime, distinctUntilChanged, map, filter } from 'rxjs/operators';
import { IStyleNode, StyleCategoryNode, StyleNode } from 'situview-webapi-client';
import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';
import { ICompany } from 'identity-webapi-client';
import { CompanyService } from 'src/app/services';

@Component({
  selector: 'app-style-category-edit',
  templateUrl: './style-category-edit.component.html',
  styleUrls: ['./style-category-edit.component.scss']
})
export class StyleCategoryEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() category: StyleCategoryNode | undefined;
  @Input() categories: StyleCategoryNode[] = [];
  @Input() styles: StyleNode[] = [];
  @Output() onSave = new EventEmitter<StyleCategoryNode>();
  @Output() onSaveStyles = new EventEmitter<StyleNode[]>();

  isAddMode: boolean = false;
  categoryForm!: FormGroup;
  filteredCategories: StyleCategoryNode[] = [];
  companies: ICompany[] = [];
  filteredCompanies: ICompany[] = [];

  isStyleListModified: boolean = false;
  baseInStyleIds: string[] = [];
  inStyleList: IListStyleNode[] = [];
  outStyleList: IListStyleNode[] = [];

  private _subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private companyService: CompanyService) {

    this._subscriptions.push(this.companyService.companies$
      .subscribe(c => {
        this.companies = c.map(x => ({ ...x }));

        if (this.categoryForm?.controls.company) {
          let value = this.categoryForm.controls.company.value;
          if (typeof value != 'string') {
            this.filteredCompanies = this.companies;
          }
          else {
            this.filteredCompanies = this.companies.filter(x => x.name?.toLowerCase().includes(value?.toLowerCase()));
          }
        }
        else {
          this.filteredCompanies = this.companies;
        }
      }));
    this.initCategoryForm();
  }

  ngOnInit(): void {
    this.refreshUI();
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((!changes.category.firstChange && changes.category)
      || (!changes.categories.firstChange && changes.categories)
      || (!changes.styles.firstChange && changes.styles)) {
      this.refreshUI();
    }
  }

  initCategoryForm() {
    this.categoryForm = this.formBuilder.group({
      parentStyleCategory: [this.getCategory(this.category?.parentStyleCategoryId)],
      company: [this.getCompany(this.category?.companyId)],
      name: [null, Validators.required],
      description: [null],
      isSingleSelect: [null, Validators.required],
      isRequired: [null, Validators.required]
    });

    this._subscriptions.push(this.categoryForm.controls.parentStyleCategory.valueChanges.pipe(
      startWith(''),
      debounceTime(100),
      distinctUntilChanged(),
      map(value => {
        let filteredCategories = this.categories.filter(x => x.id != this.category?.id);
        if (typeof value != 'string') {
          this.filteredCategories = filteredCategories;
        }
        else {
          this.filteredCategories = filteredCategories.filter(x => x.name?.toLowerCase().includes(value?.toLowerCase()));
        }
      })
    ).subscribe());

    this._subscriptions.push(this.categoryForm.controls.company.valueChanges.pipe(
      startWith(''),
      debounceTime(100),
      distinctUntilChanged(),
      map(value => {
        if ((typeof value === 'string' || value instanceof String) && value.length > 0) {
          this.filteredCompanies = this.companies.filter(x => x.name?.toLowerCase().includes(value?.toLowerCase()));
          return;
        }

        this.category!.companyId = value?.id;
        this.filteredCompanies = this.companies;
      })
    ).subscribe());

    this.categoryForm.valueChanges.subscribe(x => {
      if (!this.category) {
        return;
      }

      this.category.name = x.name;
      this.category.parentStyleCategoryId = x.parentStyleCategory?.id;
      this.category.description = x.description;
      this.category.isSingleSelect = x.isSingleSelect;
      this.category.isRequired = x.isRequired;
    });
  }

  refreshUI() {
    this.categoryForm.patchValue(this.category || {}, { emitEvent: false });
    this.categoryForm.controls.parentStyleCategory.setValue(this.getCategory(this.category?.parentStyleCategoryId));
    this.categoryForm.controls.company.setValue(this.getCompany(this.category?.companyId));

    this.initStyleLists();
  }

  initStyleLists() {
    this.baseInStyleIds = this.category?.styles?.map(x => x.id ?? '') ?? [];
    this.inStyleList = this.category?.styles
      ?.sort((a, b) => (a?.displayName ?? '').localeCompare(b?.displayName ?? ''))
      .map(x => ({ ...x, selected: false, filtered: false, categoryName: this.getCategory(x.styleCategoryId)?.name })) ?? [];
    this.outStyleList = this.styles
      ?.filter(x => !this.category?.styles?.some(y => y.id == x.id))
      .sort((a, b) => (a?.displayName ?? '').localeCompare(b?.displayName ?? ''))
      .map(x => ({ ...x, selected: false, filtered: false, categoryName: this.getCategory(x.styleCategoryId)?.name })) ?? [];
  }

  getCategory(id: string | undefined): any {
    return this.categories.find(x => x.id == id);
  }
  getCategoryName(category: StyleCategoryNode) {
    return category?.name;
  }

  getCompany(id: string | undefined): any {
    return this.companies.find(x => x.id == id);
  }
  getCompanyName(company: ICompany) {
    return company?.name;
  }

  onSubmit() {
    if (this.categoryForm.invalid) {
      return;
    }

    this.onSave.emit(this.category);
  }

  selectStyle(style: IListStyleNode) {
    style.selected = !style.selected;
  }

  selectAllStyles(isInList: boolean) {
    return isInList ?
      this.inStyleList.map(x => x.selected = true) :
      this.outStyleList.map(x => x.selected = true)
  }

  clearAllSelectedStyles(isInList: boolean) {
    return isInList ?
      this.inStyleList.map(x => x.selected = false) :
      this.outStyleList.map(x => x.selected = false)
  }

  styleSelectedInList(isInList: boolean) {
    return isInList ?
      this.inStyleList.some(x => x.selected) :
      this.outStyleList.some(x => x.selected)
  }

  getStylesSelected(isInList: boolean) {
    return isInList ?
      this.inStyleList.filter(x => x.selected)?.length :
      this.outStyleList.filter(x => x.selected)?.length;
  }

  getStyles(isInList: boolean) {
    return isInList ?
      this.inStyleList?.length :
      this.outStyleList?.length
  }

  transferList(isInRange: boolean) {
    if (isInRange) {
      this.inStyleList.filter(x => x.selected).forEach(style => {
        this.outStyleList.push(style);
        this.inStyleList = this.inStyleList.filter(inStyle => inStyle.id !== style.id);
      });
    } else {
      this.outStyleList.filter(x => x.selected).forEach(style => {
        this.inStyleList.push(style);
        this.outStyleList = this.outStyleList.filter(outStyle => outStyle.id !== style.id);
      });
    }
    this.checkIfStyleListModified();
  }

  drop(event: CdkDragDrop<IListStyleNode[]>) {
    if (event.previousContainer !== event.container) {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.checkIfStyleListModified();
    }
  };

  checkIfStyleListModified() {
    this.isStyleListModified = this.baseInStyleIds.length !== this.inStyleList.length ||
      !this.baseInStyleIds.every(style => this.inStyleList.map(x => x.id).includes(style))
  }

  updateCategoryStyles() {
    if (!this.category) {
      return;
    }

    this.onSaveStyles.emit(this.styles.filter(x => this.inStyleList.some(y => y.id == x.id)));

    // the category may have changed, update UI
    this.categoryForm.patchValue(this.category || {}, { emitEvent: false });
    this.categoryForm.controls.parentStyleCategory.setValue(this.getCategory(this.category?.parentStyleCategoryId));

    this.initStyleLists();
    this.checkIfStyleListModified();
  };
}

export interface IListStyleNode extends IStyleNode {
  [x: string]: any;
  selected: boolean;
  filtered: boolean;
  categoryName: string;
}
